import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
} from '@angular/core';
import { SpinnerComponent } from '@catch/general-ui';
import { TranslocoPipe } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { interval, Observable } from 'rxjs';
import { audit } from 'rxjs/operators';
import { AppState } from '../../../app.reducer';
import { Notification } from '../../../interfaces/notification';
import { FontawesomeService } from '../../../services/fontawesome.service';
import { ScrollEndDirective } from '../../../directives/scroll-end.directive';
import { ScrollEndRootDirective } from '../../../directives/scroll-end-root.directive';
import {
  loadNotificationsLazy,
  markAllNotificationsAsSeen,
} from '../../../store/notifications/notifications.actions';
import {
  selectNotificationsAll,
  selectNotificationsLoading,
} from '../../../store/notifications/notifications.reducer';
import { NotificationsComponent } from '../../notifications/notifications.component';
import { ButtonComponent } from '../../button/button.component';

type PopoverCloseEvent = Pick<Event, 'preventDefault' | 'stopPropagation'>;

@Component({
  selector: 'app-sidenav-notifications',
  templateUrl: './sidenav-notifications.component.html',
  styleUrls: ['./sidenav-notifications.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    TranslocoPipe,
    NotificationsComponent,
    ScrollEndDirective,
    ScrollEndRootDirective,
    SpinnerComponent,
    ButtonComponent,
  ],
})
export class SidenavNotificationsComponent implements OnInit {
  readonly closePopover = input<((event: PopoverCloseEvent) => void) | undefined>();

  private readonly store = inject(Store<AppState>);
  readonly fontawesomeService = inject(FontawesomeService);

  notifications$!: Observable<Notification[]>;
  notificationsLoading$!: Observable<boolean>;

  ngOnInit(): void {
    this.notifications$ = this.store.select(selectNotificationsAll);
    this.notificationsLoading$ = this.store
      .select(selectNotificationsLoading)
      .pipe(
        audit((isLoading) => (isLoading ? interval(0) : interval(500))),
      );
  }

  loadMoreNotifications(): void {
    this.store.dispatch(loadNotificationsLazy());
  }

  markAllNotificationsAsSeen(): void {
    this.store.dispatch(markAllNotificationsAsSeen());
  }

  dismissPopover(event?: Event): void {
    const closeEvent = event ?? this.createFallbackCloseEvent();
    this.closePopover()?.(closeEvent);
  }

  private createFallbackCloseEvent(): PopoverCloseEvent {
    return {
      preventDefault: () => undefined,
      stopPropagation: () => undefined,
    };
  }
}
